import { useState } from "react"
import { ApplicationError, ErrorCode } from "../services/ntuc/applicationError"

interface DialogInterface {
    content: string,
    title: string
    display: boolean,
    primaryButton?: string,
    secondaryButton?: string,
}

interface AlertInterface {
    content: string,
    display: boolean,
}

enum DialogButtonAction {
    BACK = `back_to_home`,
    LOGIN = `login`,
    CANCEL = `cancel`,
    DRAFT = `retrieve_draft`,
    CONTINUE = `continue`,
    LOGIN_ANOTHER = `login_another`,
    LOGIN_SINGPASS = `login_singpass`
}

const useDialogErrorHandler = (initDialog: DialogInterface): [DialogInterface, (error: ApplicationError) => void, () => void] => {
    const [dialog, setDialog] = useState<DialogInterface>(initDialog)
    const closeHandler = (): void => {
        setDialog(dialog => ({
            ...dialog,
            display: false,
        }))
    }

    const handleDialogErrors = (error: ApplicationError) => {
        switch (error.errorCode) {
            case ErrorCode.CANNOT_PROCEED:
                setDialog({
                    display: true,
                    title: `errors.cannot_proceed_title`,
                    content: `errors.cannot_proceed`,
                    primaryButton: DialogButtonAction.BACK,
                })
                break
            case ErrorCode.LINKPASS_USER_EXISTS:
                setDialog({
                    display: true,
                    title: ``,
                    content: `errors.linkpass_user`,
                    primaryButton: DialogButtonAction.LOGIN_ANOTHER,
                    secondaryButton: DialogButtonAction.CANCEL,
                })
                break
            case ErrorCode.RECORD_EXISTS_DRAFT:
                setDialog({
                    display: true,
                    title: `errors.draft_title`,
                    content: `errors.retrieve_draft`,
                    primaryButton: DialogButtonAction.DRAFT,
                    secondaryButton: DialogButtonAction.CONTINUE,

                })
                break
            case ErrorCode.RECORD_EXISTS_ACTIVE:
                if (window.myNtucConfig.authToken) {
                    setDialog({
                        display: true,
                        title: `errors.ntuc_member`,
                        content: `errors.user_link`,
                        primaryButton: DialogButtonAction.BACK,
                    })
                } else {
                    setDialog({
                        display: true,
                        title: `errors.ntuc_member`,
                        content: `errors.user_login`,
                        primaryButton: DialogButtonAction.LOGIN,
                        secondaryButton: DialogButtonAction.BACK,
                    })
                }
                break
            case ErrorCode.UNAUTHENTICATION_REQUEST:
                if (!window.myNtucConfig.authToken){
                    setDialog({
                        display: true,
                        title: `errors.authentication_request`,
                        content: `errors.user_login`,
                        primaryButton: DialogButtonAction.LOGIN_SINGPASS,
                        secondaryButton: DialogButtonAction.CANCEL,
                    })
                }
                break
        }
    }

    return [dialog, handleDialogErrors, closeHandler]
}

const useAlertErrorHandler = (initAlert?: AlertInterface | undefined): [AlertInterface | undefined, (error?: ApplicationError) => void] => {
    const [alert, setAlert] = useState<AlertInterface | undefined>(initAlert)

    const handleAlertErrors = (error?: ApplicationError) => {
        if (error) {
            switch (error.errorCode) {
                case ErrorCode.CANNOT_PROCEED:
                case ErrorCode.LINKPASS_USER_EXISTS:
                case ErrorCode.RECORD_EXISTS_DRAFT:
                case ErrorCode.RECORD_EXISTS_ACTIVE:
                    // Handled with dialog
                    break
                case ErrorCode.EMAIL_UNAVAILABLE:
                    setAlert({
                        display: true,
                        content: `errors.email`,
                    })
                    break
                case ErrorCode.VALIDATION_ERROR:
                    setAlert({
                        display: true,
                        content: error.errorDescription ?? ``,
                    })
                    break
                case ErrorCode.MYINFO_ERROR:
                    setAlert({
                        display: true,
                        content: `errors.myinfo`,
                    })
                    break
                case ErrorCode.RETRY_UPFRONT_PAYMENT_FAILED:
                    setAlert({
                        display: true,
                        content: `errors.retry_upfront_payment_failed`,
                    })
                    break
                case ErrorCode.INACTIVE_MEMBERSHIP:
                    setAlert({
                        display: true,
                        content: `errors.inactive_membership`,
                    })
                    break
                case ErrorCode.UNKNOWN_ERROR:
                default:
                    setAlert({
                        display: true,
                        content: `errors.unknown`,
                    })
                    break
            }
        }
    }

    return [alert, handleAlertErrors]
}

export { useDialogErrorHandler, useAlertErrorHandler, DialogButtonAction }
